import api from "../../utils/api";
import { normalizeDeletionCaseReviewItem } from "../../shared/utils/deletionCaseReviews";
import { normalizeOutcomeSummary } from "../../shared/utils/deletionCaseOutcomes";
import {
  mergeReviewItemsWithPricing,
  normalizeCasePricing,
} from "../../shared/utils/deletionCasePricing";

export const CASES_ENDPOINTS = {
  list: "/api/cases/",
  stats: "/api/cases/stats/",
  detail: (caseId) => `/api/cases/${caseId}/`,
};

export const CASE_STATUS_LABELS = {
  initiated: "Eingereicht",
  started: "In Bearbeitung",
  legal_action: "Rechtsschritt",
  completed: "Abgeschlossen",
  rejected: "Abgelehnt",
};

export const VIOLATION_TYPE_OPTIONS = [
  { value: "Spam", label: "Spam / Fake-Bewertung" },
  { value: "Interessenkonflikt", label: "Interessenkonflikt" },
  { value: "Beleidigung", label: "Beleidigung / Hassrede" },
  { value: "Falsche Tatsachen", label: "Falsche Tatsachenbehauptung" },
  { value: "Datenschutz", label: "Datenschutzverletzung" },
  { value: "Sonstiges", label: "Sonstiges" },
];

const asArray = (value) => (Array.isArray(value) ? value : []);

export const normalizeDeletionCase = (raw) => {
  const pricing = normalizeCasePricing(raw.pricing);
  const items = mergeReviewItemsWithPricing(
    asArray(raw.items).map((item) => normalizeDeletionCaseReviewItem(item)),
    pricing
  );
  return {
    id: raw.id,
    caseReference: raw.case_reference || "",
    userId: raw.user,
    violationType: raw.violation_type || "",
    justification: raw.justification || "",
    status: raw.status || "initiated",
    mondayItemId: raw.monday_item_id,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    pricing,
    freeReviewsApplied: raw.free_reviews_applied ?? pricing?.freeReviewsApplied ?? 0,
    paidReviewCount: raw.paid_review_count ?? pricing?.paidReviewCount ?? 0,
    caseTotal: raw.case_total ?? pricing?.caseTotal ?? null,
    items,
    outcomeSummary: normalizeOutcomeSummary(raw.outcome_summary),
  };
};

export const CASE_STATUS_FILTER_OPTIONS = [
  { value: "", label: "Alle Status" },
  ...Object.entries(CASE_STATUS_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];

export const VIOLATION_TYPE_FILTER_OPTIONS = [
  { value: "", label: "Alle Verstoßarten" },
  ...VIOLATION_TYPE_OPTIONS,
];

export function normalizeDeletionCaseStats(raw) {
  const byStatus = raw?.by_status || {};
  return {
    total: raw?.total ?? 0,
    new: raw?.new ?? 0,
    pending: raw?.pending ?? 0,
    inProgress: raw?.in_progress ?? 0,
    completed: raw?.completed ?? 0,
    rejected: raw?.rejected ?? 0,
    newThisWeek: raw?.new_this_week ?? 0,
    byStatus: {
      initiated: byStatus.initiated ?? 0,
      started: byStatus.started ?? 0,
      legal_action: byStatus.legal_action ?? 0,
      completed: byStatus.completed ?? 0,
      rejected: byStatus.rejected ?? 0,
    },
  };
}

export async function fetchDeletionCaseStats() {
  const data = await api.get(CASES_ENDPOINTS.stats, { auth: true });
  return normalizeDeletionCaseStats(data);
}

/**
 * @param {{ status?: string; statusGroup?: string; violationType?: string; search?: string }} params
 */
export async function fetchDeletionCases(params = {}) {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.statusGroup) search.set("status_group", params.statusGroup);
  if (params.createdWithinDays) {
    search.set("created_within_days", String(params.createdWithinDays));
  }
  if (params.violationType) search.set("violation_type", params.violationType);
  if (params.search?.trim()) search.set("search", params.search.trim());
  const qs = search.toString();
  const path = qs ? `${CASES_ENDPOINTS.list}?${qs}` : CASES_ENDPOINTS.list;
  const data = await api.get(path, { auth: true });
  const list = asArray(data?.results ?? data);
  return list.map(normalizeDeletionCase);
}

export async function fetchDeletionCase(caseId) {
  const data = await api.get(CASES_ENDPOINTS.detail(caseId), { auth: true });
  return normalizeDeletionCase(data);
}

/**
 * @param {{ violationType: string; justification: string; reviewIds: number[] }} payload
 */
export async function createDeletionCase(payload) {
  const body = {
    violation_type: payload.violationType,
    justification: payload.justification,
    review_ids: payload.reviewIds,
  };
  const data = await api.post(CASES_ENDPOINTS.list, body, { auth: true });
  return normalizeDeletionCase(data);
}

/** Withdraw a submitted case that has not started processing yet. */
export async function deleteDeletionCase(caseId) {
  await api.delete(CASES_ENDPOINTS.detail(caseId), { auth: true });
}

/** Public case ID shown in UI (e.g. DC-2026-000042). */
export function formatCaseId(caseOrRef) {
  if (!caseOrRef) return "—";
  if (typeof caseOrRef === "string") return caseOrRef;
  return caseOrRef.caseReference || (caseOrRef.id != null ? `DC-${caseOrRef.id}` : "—");
}

export function formatCaseError(err) {
  const data = err?.data;
  if (!data) return err?.message || "Anfrage fehlgeschlagen.";
  if (typeof data.detail === "string") return data.detail;
  if (data.review_ids) {
    const msg = Array.isArray(data.review_ids)
      ? data.review_ids.join(" ")
      : String(data.review_ids);
    return msg || "Ungültige Bewertungsauswahl.";
  }
  if (typeof data === "object") {
    const parts = Object.entries(data).map(([k, v]) => {
      const text = Array.isArray(v) ? v.join(" ") : String(v);
      return `${k}: ${text}`;
    });
    if (parts.length) return parts.join(" ");
  }
  return err?.message || "Anfrage fehlgeschlagen.";
}
